import type { ReactNode } from 'react';
import type { Choice, LiveOdds, LockedOdds, Round } from '@pons/contracts';
import { change, isOpen } from '../arena-ui';
import { isUsdReady } from '../market';
import { safeCoinLink } from './coinIdentityModel';
export const CORRECT_CALL_POINTS = 100;
export type CoinPhase = 'opening' | 'open' | 'watching' | 'settled';
export interface CoinLink {
  label: string;
  href: string;
  brand: 'pons' | 'external';
}
// One round as the Arena screen needs it. Live rounds and the demo both map onto this.
export interface CoinView {
  id: string;
  name: string;
  symbol: string;
  address: string | null;
  pairSymbol: string | null;
  pairHref: string | null;
  links: CoinLink[];
  marketCap: number | null;
  graduationCap: number | null;
  changePercent: number | null;
  resultPercent: number | null;
  baseline: number | null;
  history: number[];
  start: number;
  closesAt: number;
  settlesAt: number;
  phase: CoinPhase;
  choice: Choice | null;
  outcome: Choice | null;
  split: number | null;
  votes: number | null;
  votesUp: number | null;
  odds?: LiveOdds;
  lockedOdds?: LockedOdds;
  // Explicit internal simulation marker, also exposed to assistive technology.
  simulated: boolean;
  stale: boolean;
  note: string | null;
}
export type ArtRenderer = (coin: CoinView, className?: string) => ReactNode;
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
function positive(value: string | number | null | undefined) {
  const parsed = Number(value);
  return value == null || !Number.isFinite(parsed) || parsed <= 0
    ? null
    : parsed;
}
export function toCoinView(r: Round, now: number): CoinView {
  const twitter = safeCoinLink(r.socials?.twitter);
  const baseline = positive(r.p0Usd),
    settledPrice = positive(r.ptUsd);
  const phase: CoinPhase = r.outcome
    ? 'settled'
    : now >= r.predictionClosesAt
      ? 'watching'
      : isOpen(r, now)
        ? 'open'
        : 'opening';
  return {
    id: r.id,
    name: r.name,
    symbol: r.symbol,
    address: r.address,
    pairSymbol: r.pairToken === ZERO_ADDRESS ? 'ETH' : r.quoteSymbol,
    pairHref:
      r.pairToken === ZERO_ADDRESS
        ? null
        : 'https://robinhoodchain.blockscout.com/address/' + r.pairToken,
    links: [
      ...(twitter
        ? [{ label: 'X', href: twitter, brand: 'external' as const }]
        : []),
      {
        label: 'Pons',
        href: 'https://www.ponsfamily.com/launchpad/' + r.address,
        brand: 'pons',
      },
      ...(!twitter
        ? [
            {
              label: 'Fomo',
              href: 'https://fomo.family/tokens/robinhood/' + r.address,
              brand: 'external' as const,
            },
          ]
        : []),
    ],
    marketCap: positive(r.currentMCUsd),
    graduationCap: positive(r.capUsd),
    changePercent: change(r),
    resultPercent:
      settledPrice && baseline ? (settledPrice / baseline - 1) * 100 : null,
    baseline,
    history: (r.historyUsd || []).map(Number).filter(Number.isFinite),
    start: r.start,
    closesAt: r.predictionClosesAt,
    settlesAt: r.settlesAt,
    phase,
    choice: r.choice ?? null,
    outcome: r.outcome,
    split: r.split,
    votes: r.votes ?? null,
    votesUp: r.survivesVotes ?? null,
    odds: r.odds ?? undefined,
    lockedOdds: r.lockedOdds,
    simulated: false,
    stale: !isUsdReady(r, now) || !!r.priceError,
    note:
      r.canonical === false
        ? 'Rechecking chain data'
        : phase === 'opening'
          ? 'Verifying opening price'
          : null,
  };
}
export const isCallable = (coin: CoinView) =>
  coin.phase === 'open' && !coin.choice;
